import { useRef, useCallback } from 'react'
import { chatService } from '@/services'
import { ChatAction, ChatRequest } from '../../state/types'
import { generateRequestId } from '../../utils/chat/ids'
import { isRequestActive } from '../../utils/chat/stream'

export function useStreamingChat(dispatch: React.Dispatch<ChatAction>) {
  const activeRequestIdRef = useRef<string | null>(null)

  const cancel = useCallback(() => {
    activeRequestIdRef.current = null
  }, [])

  const send = useCallback(async (conversationId: string, request: ChatRequest) => {
    // Cancel any existing request
    cancel()

    const requestId = generateRequestId()
    activeRequestIdRef.current = requestId
    
    console.log('useStreamingChat: Starting request', { requestId, conversationId, request })
    dispatch({ type: 'SEND_START', payload: { requestId } })

    try {
      await chatService.sendStreamingMessage(
        conversationId,
        request,
        // On chunk
        (chunk) => {
          console.log('useStreamingChat: Received chunk', { requestId, chunk })
          if (!isRequestActive(activeRequestIdRef.current, requestId)) {
            console.log('useStreamingChat: Ignoring chunk for stale request', requestId)
            return
          }
          
          if (!chunk.error && chunk.chunk) {
            dispatch({ type: 'STREAM_CHUNK', payload: { chunk: chunk.chunk } })
          }
        },
        // On error
        (error) => {
          console.error('useStreamingChat: Streaming error', { requestId, error })
          if (!isRequestActive(activeRequestIdRef.current, requestId)) {
            console.log('useStreamingChat: Ignoring error for stale request', requestId)
            return
          }
          
          dispatch({ type: 'STREAM_ERROR', error })
          cancel()
        },
        // On complete
        (fullResponse) => {
          console.log('useStreamingChat: Streaming complete', { requestId, fullResponse })
          if (!isRequestActive(activeRequestIdRef.current, requestId)) {
            console.log('useStreamingChat: Ignoring completion for stale request', requestId)
            return
          }
          
          dispatch({ type: 'STREAM_COMPLETE', payload: { fullResponse } })
          cancel()
        }
      )
      console.log('useStreamingChat: sendStreamingMessage completed successfully')
    } catch (error) {
      console.error('useStreamingChat: Failed to send message', { requestId, error })
      if (isRequestActive(activeRequestIdRef.current, requestId)) {
        dispatch({ type: 'STREAM_ERROR', error })
      }
      cancel()
    }
  }, [dispatch, cancel])

  return { send, cancel }
} 